// Public holidays on the Planner (Jade, 2026-10-10). Read-only labels, never
// saved as items: nothing here syncs, rings, or counts as a task or event.
//
// The country is detected, not picked: the same /api/geo the purchase page
// uses (Vercel's x-vercel-ip-country), remembered on the phone so it works
// offline and isn't asked again for a day. Until that answer is in, the
// phone's own region (time zone, then language — deviceRegion) stands in.
//
// The dates are public/holidays/<CC>.json, generated by
// scripts/holidays/gen-holidays.js (see there for the years covered and how
// to refresh them), shipped in the app and refreshed from the website.
import { deviceRegion } from "./habitUnits";

// No clarity_ prefix: per device, so sync.js must not mirror it.
export const HOLIDAY_COUNTRY_KEY = "clarityHolidayCountry";
export const HOLIDAY_COUNTRY_AT_KEY = "clarityHolidayCountryAt";
export const COUNTRY_RECHECK_MS = 24 * 60 * 60 * 1000;

const isCountry = (cc) => typeof cc === "string" && /^[A-Z]{2}$/.test(cc);

function readLS(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}
function writeLS(key, value) {
  try { localStorage.setItem(key, value); } catch { /* private mode etc. */ }
}

// Settings → Planner Options → Holidays (Jade, 2026-10-10): "Regional" (the
// default: the detection above), a country picked by hand, or "Off". Per
// device, like the detected country.
export const HOLIDAY_MODE_KEY = "clarityHolidayMode";            // "regional" | "manual" | "off"
export const HOLIDAY_MANUAL_KEY = "clarityHolidayManualCountry";  // "JP"
export const HOLIDAY_CHOICE_EVENT = "clarity-holiday-country";

/** { mode: "regional" | "manual" | "off", manual: "JP" | "" } — manual only counts with a real code. */
export function holidayChoice() {
  const manual = readLS(HOLIDAY_MANUAL_KEY);
  const ok = isCountry(manual);
  const stored = readLS(HOLIDAY_MODE_KEY);
  const mode = stored === "off" ? "off" : stored === "manual" && ok ? "manual" : "regional";
  return { mode, manual: ok ? manual : "" };
}

/** Saves the Settings choice and tells the Planner. Returns the saved choice. */
export function setHolidayChoice(mode, manual) {
  if (isCountry(manual)) writeLS(HOLIDAY_MANUAL_KEY, manual);
  writeLS(HOLIDAY_MODE_KEY, mode === "manual" || mode === "off" ? mode : "regional");
  const choice = holidayChoice();
  try { window.dispatchEvent(new Event(HOLIDAY_CHOICE_EVENT)); } catch { /* no window */ }
  return choice;
}

/** The auto-detected country: the remembered one, else the phone's region; "" if neither. */
export function regionalHolidayCountry(env) {
  const stored = readLS(HOLIDAY_COUNTRY_KEY);
  if (isCountry(stored)) return stored;
  const r = deviceRegion(env);
  return isCountry(r) ? r : "";
}

/** The country to show holidays for right now: "" when Off, the hand-picked one, else the regional one. */
export function currentHolidayCountry(env) {
  const c = holidayChoice();
  if (c.mode === "off") return "";
  return c.mode === "manual" ? c.manual : regionalHolidayCountry(env);
}

/** "Philippines" for "PH" (in English), or the code if the phone can't name it. */
export function countryName(cc) {
  try {
    const n = new Intl.DisplayNames(["en"], { type: "region" }).of(cc);
    if (n && n !== cc) return n;
  } catch { /* old WebView */ }
  return cc;
}

/** True when the remembered country is missing or more than a day old. */
export function countryCheckDue(nowMs = Date.now()) {
  const at = Number(readLS(HOLIDAY_COUNTRY_AT_KEY)) || 0;
  return !isCountry(readLS(HOLIDAY_COUNTRY_KEY)) || nowMs - at >= COUNTRY_RECHECK_MS;
}

/** Remember a detected country; returns it, or null if it isn't a real two-letter code. */
export function rememberHolidayCountry(cc, nowMs = Date.now()) {
  if (!isCountry(cc)) return null;
  writeLS(HOLIDAY_COUNTRY_KEY, cc);
  writeLS(HOLIDAY_COUNTRY_AT_KEY, String(nowMs));
  return cc;
}

// Two copies of each country's file:
// - the one shipped inside the app (always there, works offline);
// - the website's, fetched by the Android app once per run and remembered on
//   the phone. It updates on every merge to main, so a corrected date (e.g. a
//   late Eid proclamation) or later years reach installed apps with no new
//   build. The newest copy we have wins.
// On the web the shipped copy IS the website's, so there's nothing to refresh.

// No clarity_ prefix: a per-device cache, sync.js must not mirror it.
export const HOLIDAY_CACHE_PREFIX = "clarityHolidays_";

const isHolidayMap = (d) => !!d && typeof d === "object" && !Array.isArray(d) && Object.keys(d).length > 0
  && Object.entries(d).every(([k, v]) => /^\d{4}-\d{2}-\d{2}$/.test(k) && typeof v === "string");

function readCached(cc) {
  try {
    const d = JSON.parse(readLS(HOLIDAY_CACHE_PREFIX + cc) || "null");
    return isHolidayMap(d) ? d : null;
  } catch { return null; }
}

// One load per country per app run. A failed load is forgotten so the next
// Planner visit tries again.
const loaded = new Map(); // cc -> { promise, value }
const refreshed = new Set(); // countries already fetched from the website this run

export function holidaysUrl(cc) {
  return `${process.env.PUBLIC_URL || ""}/holidays/${cc}.json`;
}

/** The already-loaded { "YYYY-MM-DD": "Name" } map for a country, or null. */
export function loadedHolidays(cc) {
  return loaded.get(cc)?.value || null;
}

const fetchJson = (fetchFn, url) => Promise.resolve()
  .then(() => fetchFn(url))
  .then(res => (res && res.ok ? res.json() : null))
  // No file for that country: the web server answers with the app's
  // index.html, which isn't JSON — so a parse failure means "none".
  .catch(() => null);

/**
 * A country's holidays: the copy last fetched from the website if the phone has
 * one, else the one shipped in the app. Resolves to {} for an unknown country
 * or when neither is there.
 */
export function loadHolidays(cc, fetchFn = (typeof fetch !== "undefined" ? fetch : null)) {
  if (!isCountry(cc)) return Promise.resolve({});
  const hit = loaded.get(cc);
  if (hit) return hit.promise;
  const entry = { value: null, promise: null };
  const cached = readCached(cc);
  entry.promise = (cached ? Promise.resolve(cached) : (fetchFn ? fetchJson(fetchFn, holidaysUrl(cc)) : Promise.resolve(null)))
    .then(data => {
      if (!isHolidayMap(data)) {
        if (!cached) loaded.delete(cc); // shipped copy missing/unreadable: retry next time
        return {};
      }
      entry.value = data;
      return data;
    });
  loaded.set(cc, entry);
  return entry.promise;
}

/**
 * Fetches the website's copy (once per country per app run). Resolves to the
 * new map when it differs from what's showing, else null. Never throws.
 */
export function refreshHolidays(cc, url, fetchFn = (typeof fetch !== "undefined" ? fetch : null)) {
  if (!isCountry(cc) || !url || !fetchFn || refreshed.has(cc)) return Promise.resolve(null);
  refreshed.add(cc);
  return fetchJson(fetchFn, url).then(data => {
    if (!isHolidayMap(data)) { refreshed.delete(cc); return null; }
    const json = JSON.stringify(data);
    const current = loaded.get(cc)?.value;
    writeLS(HOLIDAY_CACHE_PREFIX + cc, json);
    loaded.set(cc, { value: data, promise: Promise.resolve(data) });
    return current && JSON.stringify(current) === json ? null : data;
  });
}

/** Test hook: forget every loaded country. */
export function _resetHolidayCache() { loaded.clear(); refreshed.clear(); }
